import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { setToken, toToken } from "../app/features/authSlice";
import { useProxyRefreshTokenMutation } from "../app/services/auth";

/**
 * Routes that never need a standalone portal session restored — public pages and the
 * iframe-embedded login flows (which receive their tokens via postMessage from the parent).
 * Matched exactly (not by prefix), so e.g. "/login" does not also match "/login-history".
 * The root path "/" is intentionally NOT listed: it must attempt a restore so a logged-in
 * user reloading at the origin recovers their session before the index redirect runs.
 */
const PUBLIC_PATHS = [
  "/login",
  "/classic-login",
  "/email-login",
  "/otp-request",
  "/otp-verification",
  "/address-validation",
  "/404",
];

/** Only show the loader if the restore takes longer than this, to avoid a flash on quick reloads. */
const SPINNER_DELAY_MS = 250;

/** Safety cap so a stalled refresh can never leave the app on a permanent loader. */
const RESTORE_TIMEOUT_MS = 10000;

interface SessionGateProps {
  children: ReactNode;
}

/**
 * Performs a one-time silent session restore on app load.
 *
 * The access token lives only in memory, so a hard reload loses it. This gate re-hydrates it by
 * calling the cookie-based `proxyRefreshToken` endpoint (the browser sends the httpOnly refresh
 * cookie automatically). While the restore is in flight, children are withheld so protected routes
 * don't briefly redirect to /login before the session is recovered; a loader is shown only if the
 * restore is slow enough to be noticeable.
 */
export const SessionGate: FC<SessionGateProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const accessToken = useAppSelector(
    (state: RootState) => state.auth.token.accessToken
  );
  const [proxyRefreshToken] = useProxyRefreshTokenMutation();
  const attempted = useRef(false);

  const isPublicPath = PUBLIC_PATHS.includes(pathname);
  const shouldRestore = !accessToken && !isPublicPath;

  const [restoring, setRestoring] = useState(shouldRestore);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (!shouldRestore || attempted.current) {
      return;
    }
    attempted.current = true;

    let settled = false;
    let spinnerTimer: ReturnType<typeof setTimeout>;
    let timeoutTimer: ReturnType<typeof setTimeout>;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(spinnerTimer);
      clearTimeout(timeoutTimer);
      setRestoring(false);
    };

    // Reveal the loader only if the restore is slow enough to be noticeable.
    spinnerTimer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    // Never block the app indefinitely if the request stalls.
    timeoutTimer = setTimeout(finish, RESTORE_TIMEOUT_MS);

    const restore = async () => {
      try {
        const data = await proxyRefreshToken().unwrap();
        // Only hydrate the session if a real access token came back; otherwise fall through
        // to the login redirect rather than storing an empty/undefined token.
        if (data?.access_token) {
          dispatch(setToken(toToken(data)));
        }
      } catch {
        // No valid refresh cookie — the private-route guard will redirect to /login.
      } finally {
        finish();
      }
    };

    restore();

    return () => {
      clearTimeout(spinnerTimer);
      clearTimeout(timeoutTimer);
    };
  }, [shouldRestore, proxyRefreshToken, dispatch]);

  if (restoring) {
    return showSpinner ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    ) : null;
  }

  return <>{children}</>;
};

export default SessionGate;
